import React, { useRef } from 'react';
import {
    Box,
    Textarea,
    HStack,
    IconButton,
    Tooltip,
    Text,
} from '@chakra-ui/react';
import EmojiPickerButton from '../EmojiPicker/EmojiPickerButton';

const FORMATTING = [
    { label: 'Bold', syntax: '*', icon: 'B', fontWeight: 'bold' },
    { label: 'Italic', syntax: '_', icon: 'I', fontStyle: 'italic' },
    { label: 'Strike', syntax: '~', icon: 'S', textDecoration: 'line-through' },
    { label: 'Monospace', syntax: '```', icon: 'M', fontFamily: 'monospace' },
];

const WhatsAppTextarea = ({
    value,
    onChange,
    placeholder,
    rows = 4,
    maxLength,
    isReadOnly,
    bg,
    cursor,
    disabled,
    isInvalid,
    ...rest
}) => {
    const textareaRef = useRef(null);

    const applyFormat = (syntax) => {
        const textarea = textareaRef.current;
        if (!textarea || isReadOnly || disabled) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = value.slice(start, end);
        const before = value.slice(0, start);
        const after = value.slice(end);

        let newText;
        let newCursorStart;
        let newCursorEnd;

        if (selected.length > 0) {
            newText = before + syntax + selected + syntax + after;
            newCursorStart = start + syntax.length;
            newCursorEnd = end + syntax.length;
        } else {
            newText = before + syntax + syntax + after;
            newCursorStart = start + syntax.length;
            newCursorEnd = newCursorStart;
        }

        if (maxLength && newText.length > maxLength) return;

        onChange({ target: { value: newText } });

        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(newCursorStart, newCursorEnd);
        });
    };

    const insertEmoji = (emoji) => {
        const textarea = textareaRef.current;
        if (!textarea || isReadOnly || disabled) return;

        const start = textarea.selectionStart ?? value.length;
        const before = value.slice(0, start);
        const after = value.slice(start);
        const newText = before + emoji + after;

        if (maxLength && newText.length > maxLength) return;

        onChange({ target: { value: newText } });

        requestAnimationFrame(() => {
            textarea.focus();
            const pos = start + emoji.length;
            textarea.setSelectionRange(pos, pos);
        });
    };

    return (
        <Box
            border="1px solid"
            borderColor={isInvalid ? 'red.400' : 'gray.200'}
            borderRadius="md"
            overflow="hidden"
            transition="border-color 0.15s ease"
        >
            {/* Textarea */}
            <Textarea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                rows={rows}
                maxLength={maxLength}
                isReadOnly={isReadOnly}
                bg={bg}
                cursor={cursor}
                disabled={disabled}
                border="none"
                borderRadius="0"
                outline="none"
                _focus={{ boxShadow: 'none', border: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', border: 'none', outline: 'none' }}
                resize="vertical"
                sx={{
                    // Chakra's Textarea theme applies its own focus border/shadow with
                    // higher specificity than the _focus/_focusVisible props above alone
                    // can override — hence the purple line on focus despite those.
                    '&, &:focus, &:focus-visible': {
                        border: 'none !important',
                        boxShadow: 'none !important',
                        outline: 'none !important',
                    },
                }}
                {...rest}
            />

            {/* Bottom Toolbar — right-aligned */}
            <HStack
                spacing={1}
                px={2}
                py={1}
              
                justify="flex-end"
            >
                {FORMATTING.map(({ label, syntax, icon, ...style }) => (
                    <Tooltip key={label} label={label} hasArrow placement="top">
                        <IconButton
                            aria-label={label}
                            size="xs"
                            variant="ghost"
                            isDisabled={isReadOnly || disabled}
                            onClick={() => applyFormat(syntax)}
                            icon={
                                <Text fontSize="sm" sx={style}>
                                    {icon}
                                </Text>
                            }
                        />
                    </Tooltip>
                ))}

                {/* Emoji Picker */}
                <EmojiPickerButton onSelect={insertEmoji} isDisabled={isReadOnly || disabled} />
            </HStack>
        </Box>
    );
};

export default WhatsAppTextarea;
